import { createTheme } from "@mui/material";

import "@mui/material/styles";
import zIndex from "@mui/material/styles/zIndex";

declare module "@mui/material/styles" {
  interface Palette {
    gray: Palette["primary"];
  }
  interface PaletteOptions {
    gray: PaletteOptions["primary"];
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    gray: true;
  }
}

export const theme = createTheme({
  colorSchemes: {
    light: {
      palette: {
        gray: {
          main: "gray",
          dark: "dark gray",
          contrastText: "white",
        },
      },
    },
  },
  typography: {
    fontFamily: ["Raleway", "sans-serif"].join(","),
    fontSize: 16,
    h1: {
      fontSize: "4rem",
    },
    h4: {
      fontSize: "1.575rem",
      fontWeight: 700,
    },
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          "&.MuiContainer-maxWidthLg": {
            maxWidth: "1340px",
          },
          "&.MuiContainer-maxWidthXL": {
            maxWidth: "1600px",
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: "underline",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          fontWeight: 500,
          color: "#212529",
          "&.Mui-focused": {
            color: "rgba(13, 110, 253)"
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          fontWeight: 500,
          color: "#212529",
          marginBottom: "0.5rem",
          "&.Mui-disabled": {
            color: "#212529",
            "-webkit-text-fill-color": "#212529",
          },
          "&.Mui-focused": {
            color: "#0b3a5d",
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: "Raleway, sans-serif",
        },
        h1: {
          fontSize: "4rem",
        },
        h2: {
          fontSize: "2.25rem",
        },
        h3: {
          fontSize: "2rem",
        },
        h4: {
          fontSize: "1.575rem",
          fontWeight: 700,
        },
        h5: {
          fontSize: "1.125rem",
        },
        subtitle1: {
          fontSize: "1.2rem",
        },
        body1: {
          fontSize: "1rem",
          fontWeight: 400,
          lineHeight: 1.5,
        },
        paragraph: {
          fontSize: "1rem",
          marginBottom: 2,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root:({ theme }) =>  ({
          width: "100%",
          overflow: "hidden",
            transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, filter 0.3s ease",
          borderRadius: "4px",
          border: "1px solid #ced4da",
          filter: "drop-shadow(0 0 0 rgba(0,0,0,0))",
          "&focus": {
            filter: "drop-shadow(0 6px 12px rgba(0,0,0,3))",
          },
          "&.Mui-focused": {
            boxShadow: "0 0 0 2px #1cd4f570",
          },
          '&.Mui-error': {
            borderColor: theme.palette.error.main,
          },
          '&.Mui-focused.Mui-error': {
            borderColor: theme.palette.error.main,
            boxShadow: `0 0 0 2px ${theme.palette.error.main}70`,
          },
          "&::before": {
            borderBottom: "none",
            "&:focus": {},
          },
          input: {
            width: "100%",
            fontSize: "0.9rem",
            paddingLeft: "12px",
            paddingRight: "12px",
            paddingTop: "6px",
            paddingBottom: "6px",
            borderRadius: "4px",
            fontWeight: 400,
            "&:disabled": {
              backgroundColor: "#e9ecef",
              color: "#495057",
              "-webkit-text-fill-color": "#495057",
            },
          },
        }),
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          paddingX: 2,
          paddingY: 0.5,
          borderRadius: "4px",
          width: "100%",
          overflow: "hidden",
          transition: "filter 0.3s ease",
          border: "1px solid #ced4da",
          filter: "drop-shadow(0 0 0 rgba(0,0,0,0))",
          "&focus": {
            filter: "drop-shadow(0 6px 12px rgba(0,0,0,3))",
          },
          "&::before": {
            borderBottom: "none",
            "&:focus": {},
          },
        },
        input: {
          "&::focus": {
            boxShadow: "none",
            "&::before": {
              boxShadow: "0 0 0 2px rgba(42, 196, 247, 0.8)",
              borderBottom: "none",
            },
          },
        },
        disabled: {
          backgroundColor: "#e9ecef",
        },
      },
    },
    MuiTableCell: {
      variants: [
        {
          props: { variant: "head" },
          style: {
            color: "white",
          },
        },
      ],
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          paddingLeft: "12px",
          paddingTop: "6px",
          paddingBottom: "6px",
          fontSize: "0.9rem",
          height: "auto",
          lineHeight: 1.5,
          border: "1px solid #ced4da",
          "&.Mui-disabled": {
            backgroundColor: "#e9ecef",
            color: "#495057",
            "-webkit-text-fill-color": "#495057",
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        displayedRows: {
          marginBottom: 0,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&.MuiTableRow-hover": {
            cursor: "pointer",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          borderRadius: "4px",
          width: "100%",
          backgroundColor: "#fff",
          "&.Mui-disabled": {
            backgroundColor: "#e9ecef",
          },
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { variant: "outlined" },
          style: {
            color: "#0376a8",
            border: "1px solid #0376a8",
            backgroundColor: "transparent",
            "&:hover": {
              backgroundColor: "#0376a8",
              color: "#ffffff",
            },
          },
        },
         {
          props: { variant: "text" },
          style: {
            textTransform: "none",
          },
        },
      ],
    },
  },
  palette: {
    primary: {
      main: "#0376a8",
    },
    gray: {
      main: "#808080",
      dark: "#a9a9a9",
      contrastText: "#ffffff",
    },
  },
});
